import React, { useEffect, useRef } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import {
  Input, Select, Checkbox, DatePicker, TimePicker, Upload, Button,
  Radio, Space, Typography, Popover,
} from 'antd';
import { DeleteOutlined, UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FormElement, FileResponse } from '../../api/formApi';
import { EvidenceRef } from '../../types/healthcare/PatientAnalysisTypes';
import EvidenceRefList from '../healthcare/evidenceRefs/EvidenceRefList';
import { normaliseFieldType, toSingleValue, toMultiValue, fieldDisplayName } from './fieldTypes';

const { TextArea } = Input;
const { Text } = Typography;

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--mg-text-secondary)',
  display: 'block',
  marginBottom: 2,
};

const errorStyle: React.CSSProperties = {
  fontSize: 10,
  color: 'var(--mg-error)',
  display: 'block',
  marginTop: 2,
};

const readOnlyTextBox: React.CSSProperties = {
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  border: '1px solid var(--mg-border)',
  borderRadius: 4,
  padding: '4px 8px',
  background: 'var(--mg-bg-subtle)',
};

// Fixed canvas dimensions, shared by the element and by `fromDataURL` below.
// They must agree: signature_pad sizes a restored image as
// `canvas.width / devicePixelRatio` unless told otherwise, so on any HiDPI display
// an existing signature would be repainted at half scale in the top-left corner.
const SIGNATURE_WIDTH = 500;
const SIGNATURE_HEIGHT = 100;

export interface FormFieldRendererProps {
  field: FormElement;
  value: any;
  onChange: (value: any) => void;
  files?: File[];
  onFilesChange?: (files: File[]) => void;
  readOnly?: boolean;
  error?: string;
  /** Evidence references backing this field's value (e.g. an AI-filled value). */
  refs?: EvidenceRef[];
}

/**
 * Renders a single form field with compact styling. Single source of truth for
 * how each field `type` renders + reads its `options`, used by FormRenderer.
 *
 * Multiplicity is decided by the type name alone — see `./fieldTypes`:
 * - `single_select` / `select` — one option string.
 * - `multi_select` / `multiselect` — an array of option strings.
 *
 * Legacy `radio` / `checkbox` are normalised to `single_select` / `multi_select`
 * before dispatch, so this switch never sees them.
 *
 * NOTE ON SIGNATURES: the editable canvas shows a stored `value`, but it does not
 * decide WHO may change one. A consumer that must keep an existing signature
 * untouchable — a practitioner correcting a submitted patient response, say — lists
 * that field in FormRenderer's `readOnlyFieldIds`, which keeps it on the read-only
 * `<img>` branch. That policy belongs to the caller, not here.
 */
const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  field, value, onChange, files, onFilesChange, readOnly, error, refs,
}) => {
  const sigRef = useRef<ReactSignatureCanvas | null>(null);

  /**
   * Whether the canvas already shows the signature it is supposed to show.
   *
   * Set both when a stored `value` is painted in, and by `onEnd` when the user draws —
   * `onEnd` pushes its own data URL out through `onChange`, so that value comes straight
   * back as a prop one render later. Without the flag, the effect below would treat the
   * user's own stroke as an unloaded signature and repaint the canvas from an
   * asynchronous `Image` load in the middle of signing.
   */
  const signatureLoaded = useRef(false);

  /**
   * Paint a stored signature into the canvas.
   *
   * `ReactSignatureCanvas` is uncontrolled: it displays whatever has been drawn on it and
   * ignores `value` entirely. Without this, a field that already holds a signature mounts
   * BLANK and "Clear" then nulls a signed clinical record. That is reachable today —
   * `pages/patient/Form.tsx` restores saved progress into `values`, and the wizard mounts
   * only the current section, so paging back and forward past a signed field remounts it.
   *
   * Keyed on `value` rather than run once on mount because the value can arrive after the
   * first render (progress is fetched separately from the form structure). `sigRef` is
   * null for every other field type and while read-only, so this is inert there.
   */
  useEffect(() => {
    if (signatureLoaded.current || !sigRef.current || !value) return;
    signatureLoaded.current = true;
    sigRef.current.fromDataURL(value, { width: SIGNATURE_WIDTH, height: SIGNATURE_HEIGHT });
  }, [value]);

  // Heading / paragraph are display-only; their body text lives in `placeholder`.
  if (field.type === 'heading') {
    return (
      <Text style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--mg-text-secondary)', display: 'block', paddingTop: 4 }}>
        {fieldDisplayName(field)}
      </Text>
    );
  }
  if (field.type === 'paragraph') {
    return (
      <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)', display: 'block' }}>
        {fieldDisplayName(field)}
      </Text>
    );
  }

  // Only an author-written `label` is used as a fallback — a widget name never captions a field.
  const label = fieldDisplayName(field);

  const renderControl = () => {
    switch (normaliseFieldType(field.type)) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
        return (
          <Input
            size="small"
            type={field.type === 'number' ? 'number' : 'text'}
            placeholder={field.placeholder}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
          />
        );

      case 'textarea':
        return readOnly ? (
          <div style={readOnlyTextBox}>
            {value || <span style={{ color: 'var(--mg-text-secondary)' }}>No response</span>}
          </div>
        ) : (
          <TextArea
            size="small"
            autoSize={{ minRows: 3 }}
            placeholder={field.placeholder}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'select':
        return (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={value}
            onChange={(v) => onChange(v)}
            placeholder={field.placeholder}
            disabled={readOnly}
            allowClear
          >
            {field.options?.map((opt) => (
              <Select.Option key={opt} value={opt}>{opt}</Select.Option>
            ))}
          </Select>
        );

      case 'multiselect':
        return (
          <Select
            size="small"
            mode="multiple"
            style={{ width: '100%' }}
            value={toMultiValue(value) as string[]}
            onChange={(v) => onChange(v)}
            placeholder={field.placeholder}
            disabled={readOnly}
          >
            {field.options?.map((opt) => (
              <Select.Option key={opt} value={opt}>{opt}</Select.Option>
            ))}
          </Select>
        );

      // Single select (legacy `radio`): radio buttons, exactly one option.
      // Value is one option string; `toSingleValue` tolerates an array.
      case 'single_select':
        return (
          <Radio.Group value={toSingleValue(value)} onChange={(e) => onChange(e.target.value)} disabled={readOnly}>
            <Space direction="vertical" size={2}>
              {field.options?.map((opt) => (
                <Radio key={opt} value={opt} style={{ fontSize: 12 }}>{opt}</Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      // Multi select (legacy `checkbox`): checkboxes, any number of options.
      // Value is an array of option strings; `toMultiValue` tolerates a scalar.
      case 'multi_select':
        return (
          <Checkbox.Group value={toMultiValue(value) as string[]} onChange={(vals) => onChange(vals)} disabled={readOnly}>
            <Space direction="vertical" size={2}>
              {field.options?.map((opt) => (
                <Checkbox key={opt} value={opt} style={{ fontSize: 12 }}>{opt}</Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );

      case 'date':
        return (
          <DatePicker
            size="small"
            style={{ width: '100%' }}
            value={value ? dayjs(value) : null}
            onChange={(d) => onChange(d ? d.toISOString() : undefined)}
            disabled={readOnly}
          />
        );

      case 'datetime':
        return (
          <DatePicker
            size="small"
            showTime
            style={{ width: '100%' }}
            value={value ? dayjs(value) : null}
            onChange={(d) => onChange(d ? d.toISOString() : undefined)}
            disabled={readOnly}
          />
        );

      case 'time':
        return (
          <TimePicker
            size="small"
            style={{ width: '100%' }}
            value={value ? dayjs(value) : null}
            onChange={(t) => onChange(t ? t.toISOString() : undefined)}
            disabled={readOnly}
          />
        );

      case 'signature':
        if (readOnly) {
          return value ? (
            <img
              src={value}
              alt="Signature"
              style={{ maxWidth: '100%', maxHeight: 100, border: '1px solid var(--mg-border)', borderRadius: 4 }}
            />
          ) : (
            <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>No signature provided</Text>
          );
        }
        return (
          <div style={{ border: '1px solid var(--mg-border)', borderRadius: 6, overflow: 'hidden' }}>
            <ReactSignatureCanvas
              ref={(ref) => { sigRef.current = ref; }}
              canvasProps={{ width: SIGNATURE_WIDTH, height: SIGNATURE_HEIGHT }}
              onEnd={() => {
                // The canvas now holds what it should; stop the load effect repainting it.
                signatureLoaded.current = true;
                onChange(sigRef.current ? sigRef.current.toDataURL() : null);
              }}
            />
            <Button
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => { sigRef.current?.clear(); onChange(null); }}
              style={{ margin: 3 }}
            >
              Clear
            </Button>
          </div>
        );

      case 'file':
        if (readOnly) {
          const names = Array.isArray(value)
            ? (value as FileResponse[]).map((f) => f?.name).filter(Boolean).join(', ')
            : '';
          return (
            <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>
              {names || (value ? 'File uploaded' : 'No file uploaded')}
            </Text>
          );
        }
        return (
          <Upload
            multiple
            beforeUpload={(file) => {
              onFilesChange?.([...(files || []), file]);
              return false;
            }}
            onRemove={(f) => {
              onFilesChange?.((files || []).filter((_, i) => String(i) !== f.uid));
            }}
            fileList={(files || []).map((f, i) => ({ uid: String(i), name: f.name, status: 'done' as const }))}
          >
            <Button size="small" icon={<UploadOutlined />}>Upload File</Button>
          </Upload>
        );

      default:
        return (
          <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>
            Unsupported field type: {field.type}
          </Text>
        );
    }
  };

  const hasRefs = !!refs && refs.length > 0;

  return (
    <>
      <Text style={labelStyle}>
        {label}
        {field.required && <span style={{ color: 'var(--mg-error)', marginLeft: 2 }}>*</span>}
        {hasRefs && (
          <Popover content={<EvidenceRefList refs={refs} />} trigger="hover">
            <InfoCircleOutlined
              style={{ marginLeft: 4, fontSize: 11, color: 'var(--mg-primary)', cursor: 'help' }}
            />
          </Popover>
        )}
      </Text>
      {renderControl()}
      {error && <Text style={errorStyle}>{error}</Text>}
    </>
  );
};

export default FormFieldRenderer;
